import React, { useEffect, useRef, useState } from "react";

import cx from "classnames";

import { useMeetingModalContext } from "~/meetings/MeetingModal/MeetingModalContext";

import navPanelStyles from "~/nav/panels/shared/NavPanel.module.css";
import panelSectionStyles from "~/nav/panels/shared/PanelSection.module.css";
import panelStyles from "~/ui/CollapsiblePanel/CollapsiblePanel.module.css";

import styles from "./AskFellowPanel.module.css";

const SKELETON_ITEM_HEIGHT = 180; // Adjusted height with margins
const MIN_ITEMS = 5; // Minimum number of blocks to display

const SkeletonBlock = ({
	rowCount,
	headerWidth,
	showDot = true,
}: {
	rowCount: number;
	headerWidth: string;
	showDot?: boolean;
}) => {
	const rows = Array(rowCount)
		.fill(null)
		.map((_, index) => {
			const lineWidth = 50 + (index % 3) * 10;

			return (
				// eslint-disable-next-line react/no-array-index-key
				<div key={index} className={styles.skeletonRow}>
					<div className={styles.skeletonSquare}></div>
					<div className={styles.skeletonLine} style={{ width: `${lineWidth}%` }}></div>
				</div>
			);
		});

	return (
		<div className={styles.skeletonBlock}>
			<div className={styles.skeletonBar} style={{ width: headerWidth }}></div>
			{showDot && <div className={styles.skeletonDot}></div>}
			<div className={styles.skeletonRows}>{rows}</div>
		</div>
	);
};

export const AskFellowPanelSkeleton = () => {
	const containerRef = useRef<HTMLDivElement>(null);
	const [blockCount, setBlockCount] = useState(MIN_ITEMS);
	const meetingModalContext = useMeetingModalContext();
	const inModal = meetingModalContext !== null;

	// Calculate how many blocks to render based on container height
	useEffect(() => {
		const updateBlockCount = () => {
			if (containerRef.current) {
				const containerHeight = window.innerHeight - 100; // Subtract header and padding
				const count = Math.max(MIN_ITEMS, Math.ceil(containerHeight / SKELETON_ITEM_HEIGHT));
				setBlockCount(count);
			}
		};

		updateBlockCount();
		window.addEventListener("resize", updateBlockCount);

		return () => {
			window.removeEventListener("resize", updateBlockCount);
		};
	}, []);

	const blockConfigs = [
		{ rowCount: 4, headerWidth: "35%", showDot: false },
		{ rowCount: 4, headerWidth: "30%", showDot: true },
		{ rowCount: 3, headerWidth: "28%", showDot: true },
		{ rowCount: 6, headerWidth: "25%", showDot: true },
		{ rowCount: 2, headerWidth: "23%", showDot: true },
	];

	// Generate blocks with varying row counts
	const blocks = Array(blockCount)
		.fill(null)
		.map((_, index) => {
			const configIndex = index % blockConfigs.length;
			const { rowCount, headerWidth, showDot } = blockConfigs[configIndex];

			// eslint-disable-next-line react/no-array-index-key
			return <SkeletonBlock key={index} rowCount={rowCount} headerWidth={headerWidth} showDot={showDot} />;
		});

	return (
		<section
			className={cx(navPanelStyles.panel, panelStyles.panelBase, styles.mainPanel, { [styles.inModal]: inModal })}
		>
			<header className={navPanelStyles.panelHeader}>
				<div className={styles.skeletonHeader} />
			</header>
			<div className={cx(navPanelStyles.main, styles.mainContentArea)}>
				<div
					className={cx(panelSectionStyles.panelSection, panelSectionStyles.panelSectionPrimary, styles.contentSection)}
				>
					<div className={panelSectionStyles.panelSectionContent}>
						<div ref={containerRef} className={styles.skeletonContainer}>
							{blocks}
						</div>
					</div>
				</div>
			</div>
		</section>
	);
};

export default AskFellowPanelSkeleton;
